import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Badge } from "../components/ui/badge";
import { AlertTriangle, Clock, DollarSign, User } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STAGE_CONFIG = {
  order_placed: { label: "Order Placed", color: "bg-blue-500", border: "border-blue-500/30", bg: "bg-blue-500/5" },
  proof_sent: { label: "Proof Sent", color: "bg-violet-500", border: "border-violet-500/30", bg: "bg-violet-500/5" },
  proof_approved: { label: "Proof Approved", color: "bg-cyan-500", border: "border-cyan-500/30", bg: "bg-cyan-500/5" },
  in_production: { label: "In Production", color: "bg-amber-500", border: "border-amber-500/30", bg: "bg-amber-500/5" },
  quality_check: { label: "Quality Check", color: "bg-emerald-500", border: "border-emerald-500/30", bg: "bg-emerald-500/5" },
  ready_to_ship: { label: "Ready to Ship", color: "bg-teal-500", border: "border-teal-500/30", bg: "bg-teal-500/5" },
  shipped: { label: "Shipped", color: "bg-indigo-500", border: "border-indigo-500/30", bg: "bg-indigo-500/5" },
  on_hold: { label: "On Hold", color: "bg-red-500", border: "border-red-500/30", bg: "bg-red-500/5" },
  blocked: { label: "Blocked", color: "bg-red-700", border: "border-red-700/30", bg: "bg-red-700/5" },
};

const STAGES = Object.keys(STAGE_CONFIG);

export default function KanbanBoard({ onOpenOrder }) {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draggedOrder, setDraggedOrder] = useState(null);

  const fetchOrders = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/oms/orders`, { withCredentials: true });
      setOrders(data);
    } catch (err) {
      console.error("Failed to fetch orders:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  const handleDrop = async (stage) => {
    if (!draggedOrder || draggedOrder.status === stage) { setDraggedOrder(null); return; }

    if (stage === "on_hold" || stage === "blocked") {
      const reason = prompt(`Reason for ${stage.replace("_", " ")}:`);
      if (!reason) { setDraggedOrder(null); return; }
      try {
        await axios.patch(`${API}/oms/orders/${draggedOrder.id}`, { status: stage, hold_reason: reason }, { withCredentials: true });
        fetchOrders();
      } catch (err) {
        alert(err.response?.data?.detail || "Failed to update");
      }
    } else {
      try {
        await axios.patch(`${API}/oms/orders/${draggedOrder.id}`, { status: stage }, { withCredentials: true });
        fetchOrders();
      } catch (err) {
        alert(err.response?.data?.detail || "Failed to update");
      }
    }
    setDraggedOrder(null);
  };

  const getDueColor = (dueDate) => {
    if (!dueDate) return "";
    try {
      const due = new Date(dueDate);
      const now = new Date();
      const diff = (due - now) / (1000 * 60 * 60 * 24);
      if (diff < 0) return "ring-2 ring-red-500";
      if (diff < 2) return "ring-2 ring-yellow-500";
      return "";
    } catch { return ""; }
  };

  const getPaymentBadge = (status) => {
    const map = {
      paid: "bg-green-500/20 text-green-400",
      partial: "bg-yellow-500/20 text-yellow-400",
      unpaid: "bg-red-500/20 text-red-400",
    };
    return map[status] || "bg-zinc-700 text-zinc-400";
  };

  if (loading) return <div className="flex items-center justify-center h-64 text-zinc-500">Loading kanban...</div>;

  return (
    <div data-testid="kanban-board">
      <h2 className="text-2xl font-bold text-white mb-4">Kanban Board</h2>
      <div className="flex gap-3 overflow-x-auto pb-4" style={{ minHeight: "60vh" }}>
        {STAGES.map((stage) => {
          const config = STAGE_CONFIG[stage];
          const stageOrders = orders.filter((o) => o.status === stage);
          return (
            <div
              key={stage}
              className={`flex-shrink-0 w-64 rounded-xl border ${config.border} ${config.bg} p-3`}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => handleDrop(stage)}
              data-testid={`kanban-col-${stage}`}
            >
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className={`w-2.5 h-2.5 rounded-full ${config.color}`} />
                  <span className="text-sm font-semibold text-zinc-200">{config.label}</span>
                </div>
                <Badge variant="secondary" className="bg-zinc-800 text-zinc-400 text-xs">{stageOrders.length}</Badge>
              </div>
              <div className="space-y-2 min-h-[100px]">
                {stageOrders.map((order) => (
                  <div
                    key={order.id}
                    draggable
                    onDragStart={() => setDraggedOrder(order)}
                    onClick={() => onOpenOrder(order.id)}
                    className={`bg-zinc-900 rounded-lg p-3 border border-zinc-800 cursor-pointer hover:border-zinc-600 transition ${getDueColor(order.due_date)}`}
                    data-testid={`order-card-${order.order_number}`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs font-bold text-orange-400">{order.order_number}</span>
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${getPaymentBadge(order.payment_status)}`}>
                        {order.payment_status}
                      </span>
                    </div>
                    <p className="text-sm text-white font-medium truncate">{order.customer_name}</p>
                    <p className="text-xs text-zinc-500 truncate">
                      {order.items?.map((i) => i.name).join(", ")}
                    </p>
                    <div className="flex items-center justify-between mt-2">
                      <span className="text-xs text-zinc-400 flex items-center gap-1">
                        <DollarSign className="w-3 h-3" />${order.total_amount?.toLocaleString()}
                      </span>
                      {order.due_date && (
                        <span className="text-xs text-zinc-500 flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {new Date(order.due_date).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                        </span>
                      )}
                    </div>
                    {order.hold_reason && (
                      <div className="mt-2 text-xs text-red-400 bg-red-500/10 rounded px-2 py-1 flex items-center gap-1">
                        <AlertTriangle className="w-3 h-3" /> {order.hold_reason}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
